<div class="relative flex h-full w-full flex-col bg-white shadow-2xl">

  <!-- Header -->
  <div class="flex w-100 items-center gap-4 border-b border-gray-100 px-5 py-3">
    <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gray-100">
      <app-icon class="!text-[20px] !h-[20px] !w-[20px] !leading-none text-gray-500">settings</app-icon>
    </div>
    <div class="flex-1">
      <div class="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{{ 'board_v3_board_settings' | translate }}</div>
      <div class="text-base font-bold text-gray-900">{{ 'board_v3_fields_title' | translate }}</div>
    </div>
    <div class="flex items-center gap-2">
      <div class="flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
        <app-icon class="!text-[13px] !h-[13px] !w-[13px] !leading-none">grid_view</app-icon>
        {{ 'board_v3_total_count' | translate: { count: totalCount } }}
      </div>
      <div class="flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-600">
        <app-icon class="!text-[13px] !h-[13px] !w-[13px] !leading-none">filter_list</app-icon>
        {{ 'board_v3_system_count' | translate: { count: systemCount } }}
      </div>
      <div class="flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-600">
        <app-icon class="!text-[13px] !h-[13px] !w-[13px] !leading-none">add</app-icon>
        {{ 'board_v3_custom_count' | translate: { count: customCount } }}
      </div>
    </div>
  </div>

  <!-- Body -->
  <div class="flex flex-1 min-h-0 w-100">

    <!-- Left: field list -->
    <div class="flex w-[380px] shrink-0 flex-col border-r border-gray-100">
      <div class="flex w-full items-center justify-between px-4 py-3">
        <span class="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{{ 'board_v3_deal_form_fields' | translate }}</span>
        <span class="text-xs text-gray-400">{{ totalCount }}</span>
      </div>

      <div class="crm-scroll w-full flex-1 overflow-y-auto px-4 pb-3">
        @if (loading) {
          <div class="flex w-full items-center justify-center py-10 text-gray-300">
            <app-icon class="!text-[28px] animate-spin">progress_activity</app-icon>
          </div>
        }
        <div
          cdkDropList
          class="flex w-full flex-col gap-1.5"
          [cdkDropListData]="fields"
          [cdkDropListDisabled]="saving || loading"
          (cdkDropListDropped)="onFieldDrop($event)"
        >
          @for (item of allItems; track item.id ?? item.key) {
            <div
              cdkDrag
              cdkDragLockAxis="y"
              [cdkDragData]="item"
              [cdkDragDisabled]="saving || loading || item.is_system"
              class="flex w-full cursor-pointer items-center gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2.5 hover:border-blue-200 hover:bg-blue-50/40"
              [class.!border-blue-400]="selectedField?.id === item?.id"
              [class.!bg-blue-50]="selectedField?.id === item?.id"
              (click)="selectField(item)"
            >
              <app-icon
                cdkDragHandle
                class="!text-[16px] !h-[16px] !w-[16px] !leading-none shrink-0 text-gray-300"
                [class.cursor-grab]="!item.is_system"
              >drag_indicator</app-icon>
              <app-icon class="!text-[18px] !h-[18px] !w-[18px] !leading-none shrink-0 text-gray-400">{{ getFieldIcon(item) }}</app-icon>
              <div class="min-w-0 flex-1">
                <div class="flex flex-wrap items-center gap-1.5">
                  <span class="text-sm font-medium text-gray-800">
                    @if (item.is_system) {
                      {{ item.name | translate }}
                    } @else {
                      {{ item.name }}
                    }
                  </span>
                  @if (item.is_required) {
                    <span class="rounded px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-orange-100 text-orange-600">{{ 'board_v3_required_short' | translate }}</span>
                  }
                  @if (item.is_system) {
                    <span class="rounded px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-blue-100 text-blue-600">{{ 'board_v3_system_short' | translate }}</span>
                  }
                </div>
                <div class="text-xs text-gray-400">{{ item.input_type }}</div>
              </div>
              @if (!item.is_system) {
                <button
                  type="button"
                  class="inline-flex h-7 w-7 shrink-0 items-center justify-center !rounded-sm text-gray-300 hover:bg-red-50 hover:text-red-400"
                  (click)="$event.stopPropagation(); deleteCustomField(item)"
                >
                  <app-icon class="!text-[16px] !h-[16px] !w-[16px] !leading-none">delete_outline</app-icon>
                </button>
              }
            </div>
          }
        </div>
      </div>

      <div class="w-full px-4 pb-4 pt-2">
        <button
          type="button"
          class="flex w-full items-center justify-center gap-1.5 !rounded-md border-2 border-dashed border-gray-200 py-2.5 text-sm text-gray-400 hover:border-blue-300 hover:text-blue-500"
          (click)="onAddCustomField()"
        >
          <app-icon class="!text-[15px] !h-[15px] !w-[15px] !leading-none">add</app-icon>
          {{ 'board_v3_add_field' | translate }}
        </button>
      </div>
    </div>

    <!-- Right: empty state / editor -->
    <div class="flex flex-1 flex-col bg-gray-50/60">
      @if (!selectedField && !createField) {
        <div class="flex h-full w-full flex-col items-center justify-center gap-4 text-center">
          <div class="flex h-20 w-20 items-center justify-center rounded-3xl bg-white shadow-sm ring-1 ring-gray-100">
            <app-icon class="!text-[38px] !h-[38px] !w-[38px] !leading-none text-gray-300">tune</app-icon>
          </div>
          <div>
            <div class="text-lg font-semibold text-gray-700">{{ 'board_v3_select_field_title' | translate }}</div>
            <div class="mt-1 text-sm text-gray-400 leading-relaxed">
              {{ 'board_v3_select_field_hint_1' | translate }}<br>{{ 'board_v3_select_field_hint_2' | translate }}
            </div>
          </div>
          <button
            type="button"
            class="mt-1 inline-flex items-center gap-2 whitespace-nowrap !rounded-md bg-emerald-500 px-6 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600"
            (click)="onAddCustomField()"
          >
            <app-icon class="!text-[15px] !h-[15px] !w-[15px] !leading-none">add</app-icon>
            {{ 'board_v3_add_custom_field' | translate }}
          </button>
        </div>
      } @else {
        @let field = createField ?? selectedField;
        <div class="flex h-full flex-col">

          <!-- Editor header -->
          <div class="border-b border-gray-100 bg-white px-8 py-3">
            @if (createField) {
              <div class="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{{ 'board_v3_new_field' | translate }}</div>
              <div class="mt-0.5 text-xl font-bold text-gray-900">{{ 'board_v3_custom_field' | translate }}</div>
            } @else {
              <div class="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{{ 'board_v3_edit_field' | translate }}</div>
              <div class="mt-0.5 text-xl font-bold text-gray-900">
                @if (field.is_system) { {{ field.name | translate }} } @else { {{ field.name }} }
              </div>
            }
          </div>

          <!-- Editor body -->
          <div class="crm-scroll flex-1 overflow-y-auto px-8 py-6">

            <!-- Field name -->
            <div class="mb-5">
              <app-input
                [formControl]="form.controls.name"
                [label]="'board_v3_field_name' | translate"
                [placeholder]="'board_v3_field_name_placeholder' | translate"
                [disabled]="field.is_system"
              />
            </div>

            <!-- Identifier -->
            <div class="mb-6">
              @if (createField) {
                <span class="mb-2 block text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                  {{ 'board_v3_identifier' | translate }}
                </span>
                <div class="flex items-stretch overflow-hidden rounded-lg border focus-within:ring-2 focus-within:ring-blue-100"
                     [class.border-gray-200]="!form.controls.identifier.invalid || !form.controls.identifier.touched"
                     [class.border-red-400]="form.controls.identifier.invalid && form.controls.identifier.touched">
                  <span class="flex select-none items-center border-r border-gray-200 bg-gray-50 px-3 font-mono text-sm text-gray-500">x_</span>
                  <input
                    type="text"
                    [formControl]="form.controls.identifier"
                    placeholder="field_key"
                    class="flex-1 bg-transparent px-3.5 py-2.5 font-mono text-sm text-gray-800 outline-none placeholder:text-gray-300"
                    (input)="onIdentifierInput($event)"
                    (blur)="onIdentifierBlur()"
                  />
                </div>
                @if (form.controls.identifier.invalid && form.controls.identifier.touched) {
                  <p class="mt-1 text-xs text-red-500">
                    @if (form.controls.identifier.hasError('required')) { {{ 'field_validation.identifier_required' | translate }} }
                    @else if (form.controls.identifier.hasError('pattern')) { {{ 'field_validation.identifier_pattern' | translate }} }
                    @else if (form.controls.identifier.hasError('maxlength')) { {{ 'field_validation.identifier_maxlength' | translate }} }
                    @else if (form.controls.identifier.hasError('duplicate')) { {{ 'field_validation.identifier_duplicate' | translate }} }
                  </p>
                } @else {
                  <p class="mt-1.5 text-xs text-gray-400">{{ 'board_v3_identifier_hint' | translate }}</p>
                }
              } @else {
                <app-input
                  [formControl]="form.controls.identifier"
                  [disabled]="true"
                  [label]="'board_v3_identifier' | translate"
                />
              }
            </div>

            <!-- Field type -->
            <div class="mb-6">
              <label class="mb-2 block text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                {{ 'board_v3_type' | translate }}
              </label>
              <div class="grid grid-cols-3 gap-2">
                @for (type of fieldTypes; track type.input_type) {
                  <button
                    type="button"
                    class="flex items-center gap-2.5 !rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-600 transition-colors hover:border-blue-200 hover:bg-blue-50/40 disabled:cursor-not-allowed disabled:opacity-50"
                    [class.!border-blue-400]="field.input_type === type.input_type"
                    [class.!bg-blue-50]="field.input_type === type.input_type"
                    [class.!text-blue-700]="field.input_type === type.input_type"
                    [class.font-semibold]="field.input_type === type.input_type"
                    [disabled]="field.is_system || !!selectedField"
                    (click)="selectType(type)"
                  >
                    <app-icon class="!text-[17px] !h-[17px] !w-[17px] !leading-none shrink-0">{{ type.icon }}</app-icon>
                    <span class="truncate">{{ type.name }}</span>
                  </button>
                }
              </div>
              @if (typeInvalid) {
                <p class="mt-1 text-xs text-red-500">{{ 'field_validation.type_required' | translate }}</p>
              }
              @if (field.is_system || selectedField) {
                <p class="mt-2 text-xs text-gray-400">{{ 'board_v3_type_immutable_hint' | translate }}</p>
              }
            </div>

            <!-- Default value -->
            @if (field.input_type) {
              <div class="mb-6">
                @if (field.input_type === 'boolean' || field.input_type === 'checkbox') {
                  <div class="rounded-xl border border-gray-200 bg-white px-4 py-3">
                    <div class="flex items-center justify-between gap-4">
                      <div class="min-w-0">
                        <div class="text-sm font-semibold text-gray-800">{{ 'default_value' | translate }}</div>
                      </div>
                      <p-toggleswitch [formControl]="form.controls.default_value" />
                    </div>
                  </div>
                } @else if (field.input_type === 'select') {
                  <label class="mb-1 block text-sm font-medium text-gray-700">{{ 'default_value' | translate }}</label>
                  <app-select
                    [formControl]="form.controls.default_value"
                    [options]="getSelectOptions()"
                    optionLabel="label"
                    optionValue="value"
                    [searchInInput]="true"
                    [showClear]="true"
                    [appendTo]="'body'"
                    [placeholder]="'optional' | translate"
                  />
                } @else if (field.input_type === 'date') {
                  <div class="rounded-xl border border-gray-200 bg-white p-4">
                    <div class="flex items-center justify-between gap-4">
                      <div class="min-w-0">
                        <div class="text-sm font-semibold text-gray-800">{{ 'board_v3_today' | translate }}</div>
                        <div class="mt-0.5 text-xs text-gray-400">{{ 'board_v3_date_company_timezone_hint' | translate }}</div>
                      </div>
                      <p-toggleswitch [formControl]="form.controls.default_today" />
                    </div>
                    <div class="mt-4 grid grid-cols-2 gap-3">
                      <div class="block text-xs text-gray-500">
                        <span>{{ 'board_v3_date' | translate }}</span>
                        <p-datepicker
                          styleClass="mt-1 w-full"
                          [fluid]="true"
                          dataType="string"
                          dateFormat="yy-mm-dd"
                          [showButtonBar]="true"
                          [appendTo]="'body'"
                          panelStyleClass="board-v3-datepicker-panel"
                          inputStyleClass="h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800 outline-none focus:border-blue-400 disabled:cursor-not-allowed disabled:bg-gray-50"
                          [formControl]="form.controls.default_value"
                          [disabled]="form.controls.default_today.value"
                        />
                      </div>
                      <div class="block text-xs text-gray-500">
                        <span>{{ 'board_v3_time_optional' | translate }}</span>
                        <p-datepicker
                          styleClass="mt-1 w-full"
                          [fluid]="true"
                          dataType="string"
                          [timeOnly]="true"
                          hourFormat="24"
                          [stepMinute]="1"
                          [appendTo]="'body'"
                          panelStyleClass="board-v3-datepicker-panel"
                          inputStyleClass="h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800 outline-none focus:border-blue-400 disabled:cursor-not-allowed disabled:bg-gray-50"
                          [formControl]="form.controls.default_time"
                          [disabled]="!form.controls.default_value.value || form.controls.default_today.value"
                        />
                      </div>
                    </div>
                  </div>
                } @else if (field.input_type === 'price') {
                  <label class="mb-1 block text-sm font-medium text-gray-700">{{ 'default_value' | translate }}</label>
                  <div class="flex items-center gap-2">
                    <input
                      type="text"
                      inputmode="decimal"
                      [formControl]="form.controls.default_value"
                      [placeholder]="'optional' | translate"
                      class="h-10 min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800 outline-none focus:border-blue-400"
                      [class.border-red-400]="form.controls.default_value.invalid && form.controls.default_value.touched"
                      (input)="onPriceInput($event)"
                      (blur)="onDefaultValueBlur(field.input_type)"
                    />
                    <app-select
                      class="w-[132px] shrink-0"
                      [formControl]="form.controls.default_currency"
                      [options]="currencyOptions"
                      optionLabel="label"
                      optionValue="value"
                      [fluid]="false"
                      [appendTo]="'body'"
                    />
                  </div>
                  @if (form.controls.default_value.invalid && form.controls.default_value.touched) {
                    <p class="mt-1 text-xs text-red-500">
                      {{ 'input_errors.price' | translate: { value: getDefaultValueMaxLength(field.input_type) } }}
                    </p>
                  }
                } @else if (field.input_type === 'textarea') {
                  <label class="mb-1 block text-sm font-medium text-gray-700">{{ 'default_value' | translate }}</label>
                  <textarea
                    rows="4"
                    [attr.maxlength]="getDefaultValueMaxLength(field.input_type)"
                    [formControl]="form.controls.default_value"
                    [placeholder]="'optional' | translate"
                    class="min-h-24 w-full resize-y rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none focus:border-blue-400"
                    [class.border-red-400]="form.controls.default_value.invalid && form.controls.default_value.touched"
                    (blur)="onDefaultValueBlur(field.input_type)"
                  ></textarea>
                  @if (form.controls.default_value.invalid && form.controls.default_value.touched) {
                    <p class="mt-1 text-xs text-red-500">
                      {{ 'input_errors.maxlength' | translate: { value: getDefaultValueMaxLength(field.input_type) } }}
                    </p>
                  }
                } @else {
                  <label class="mb-1 block text-sm font-medium text-gray-700">{{ 'default_value' | translate }}</label>
                  <input
                    type="text"
                    [attr.maxlength]="field.input_type === 'text' ? getDefaultValueMaxLength(field.input_type) : null"
                    [attr.inputmode]="field.input_type === 'number' || field.input_type === 'price' ? 'decimal' : field.input_type === 'phone' ? 'tel' : field.input_type === 'email' ? 'email' : field.input_type === 'link' ? 'url' : null"
                    [formControl]="form.controls.default_value"
                    [placeholder]="'optional' | translate"
                    class="h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800 outline-none focus:border-blue-400"
                    [class.border-red-400]="form.controls.default_value.invalid && form.controls.default_value.touched"
                    (input)="field.input_type === 'price' && onPriceInput($event)"
                    (blur)="onDefaultValueBlur(field.input_type)"
                  />
                  @if (form.controls.default_value.invalid && form.controls.default_value.touched) {
                    <p class="mt-1 text-xs text-red-500">
                      {{ (field.input_type === 'number' ? 'input_errors.number' : field.input_type === 'price' ? 'input_errors.price' : field.input_type === 'phone' ? 'input_errors.phone' : field.input_type === 'email' ? 'input_errors.email' : field.input_type === 'link' ? 'input_errors.link' : form.controls.default_value.hasError('singleLine') ? 'input_errors.single_line' : 'input_errors.maxlength') | translate: { value: getDefaultValueMaxLength(field.input_type) } }}
                    </p>
                  }
                }
              </div>
            }

            @if (field.input_type === 'select') {
              <div class="mb-6 rounded-xl border border-gray-200 bg-white p-4">
                <div class="mb-3 text-sm font-semibold text-gray-800">{{ 'board_v3_options' | translate }}</div>
                <div class="flex gap-2">
                  <input
                    type="text"
                    [formControl]="optionInput"
                    maxlength="128"
                    [placeholder]="'board_v3_add_option_placeholder' | translate"
                    class="h-10 min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800 outline-none focus:border-blue-400"
                    (keydown)="onOptionInputKeydown($event)"
                  />
                  <button
                    type="button"
                    class="inline-flex h-10 items-center gap-1 rounded-lg bg-blue-500 px-3 text-sm font-semibold text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:bg-gray-300"
                    [disabled]="!optionInput.value.trim()"
                    (click)="addOption()"
                  >
                    <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">add</app-icon>
                    {{ 'board_v3_add' | translate }}
                  </button>
                </div>
                @if (form.controls.options.value.length) {
                  <div class="mt-3 flex flex-wrap gap-2">
                    @for (option of form.controls.options.value; track option) {
                      <span class="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1.5 text-sm text-gray-700">
                        {{ option }}
                        <button
                          type="button"
                          class="inline-flex h-4 w-4 items-center justify-center rounded-full text-gray-400 hover:bg-red-100 hover:text-red-500"
                          [attr.aria-label]="'board_v3_delete_option' | translate: { option: option }"
                          (click)="removeOption(option)"
                        >
                          <app-icon class="!h-3 !w-3 !text-[12px] !leading-none">close</app-icon>
                        </button>
                      </span>
                    }
                  </div>
                } @else {
                  <p class="mt-2 text-xs text-gray-400">{{ 'board_v3_select_options_required_hint' | translate }}</p>
                }
              </div>
            }

            <!-- Parameters -->
            <div>
              <div class="mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">{{ 'board_v3_parameters' | translate }}</div>
              <div class="flex flex-col divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white">
                <div class="flex items-center justify-between gap-4 px-4 py-3">
                  <div class="min-w-0">
                    <div class="text-sm font-semibold text-gray-800">{{ 'board_v3_required_field' | translate }}</div>
                    <div class="mt-0.5 text-xs text-gray-400">{{ 'board_v3_required_field_hint' | translate }}</div>
                  </div>
                  <p-toggleswitch [(ngModel)]="field.is_required" [disabled]="field.input_type === 'checkbox'" (ngModelChange)="onRequiredChange($event)" />
                </div>
                <div class="flex items-center justify-between gap-4 px-4 py-3">
                  <div class="min-w-0">
                    <div class="text-sm font-semibold text-gray-800">{{ 'board_v3_readonly' | translate }}</div>
                    <div class="mt-0.5 text-xs text-gray-400">{{ 'board_v3_readonly_hint' | translate }}</div>
                  </div>
                  <p-toggleswitch [(ngModel)]="field.is_readonly" [disabled]="field.is_required" />
                </div>
                <div class="flex items-center justify-between gap-4 px-4 py-3">
                  <div class="min-w-0">
                    <div class="text-sm font-semibold text-gray-800">{{ 'board_v3_hidden_field' | translate }}</div>
                    <div class="mt-0.5 text-xs text-gray-400">{{ 'board_v3_hidden_field_hint' | translate }}</div>
                  </div>
                  <p-toggleswitch [(ngModel)]="field.is_hidden" [disabled]="field.is_required" />
                </div>
              </div>
            </div>

          </div>

          <!-- Editor footer -->
          <div class="flex items-center justify-end gap-3 border-t border-gray-100 bg-white px-8 py-4">
            <app-button [btnType]="'cancel'" [disabled]="saving" (onChangeClick)="onCancel()">{{ 'cancel' | translate }}</app-button>
            <app-button [disabled]="saving || isSelectOptionsInvalid" (onChangeClick)="onSave()">
              @if (saving) {
                <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/50 border-t-white"></span>
              } @else {
                {{ 'save' | translate }}
              }
            </app-button>
          </div>

        </div>
      }
    </div>

  </div>

  @if (conflict) {
    <div class="absolute inset-0 z-40 flex items-center justify-center bg-slate-950/35 p-5">
      <div class="w-full max-w-md rounded-lg border border-slate-200 bg-white p-5 shadow-2xl">
        <div class="flex items-start gap-3">
          <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700">
            <app-icon class="!h-5 !w-5 !text-[20px] !leading-5">sync_problem</app-icon>
          </div>
          <div class="min-w-0">
            <h2 class="m-0 text-base font-semibold text-slate-950">{{ 'board_v3_fields_conflict_title' | translate }}</h2>
            <p class="mb-0 mt-1 text-sm leading-5 text-slate-600">
              {{ 'board_v3_fields_conflict_description' | translate: { name: conflict.authorName } }}
            </p>
            @if (conflict.changes.length) {
              <p class="mb-0 mt-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {{ 'board_v3_conflict_changed_fields' | translate }}
              </p>
              <ul class="mb-0 mt-1 flex flex-wrap gap-1.5 p-0">
                @for (change of conflict.changes; track change) {
                  <li class="list-none rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">
                    {{ change }}
                  </li>
                }
              </ul>
            }
          </div>
        </div>

        <div class="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-center">
          <app-button
            btnType="cancel"
            styleType="rounded"
            [customClass]="'!h-11 min-w-[148px] !rounded-lg !bg-rose-600 !px-5 text-sm font-semibold !leading-none hover:!bg-rose-700 active:!bg-rose-800 [--mat-text-button-hover-state-layer-opacity:0] [--mat-text-button-focus-state-layer-opacity:0] [--mat-text-button-pressed-state-layer-opacity:0]'"
            (onChangeClick)="discardMyChanges()"
          >
            {{ 'board_v3_conflict_discard_mine' | translate }}
          </app-button>
          <app-button
            styleType="rounded"
            [customClass]="'!h-11 min-w-[148px] !rounded-lg !bg-emerald-600 !px-5 text-sm font-semibold !leading-none hover:!bg-emerald-700 active:!bg-emerald-800 [--mat-text-button-hover-state-layer-opacity:0] [--mat-text-button-focus-state-layer-opacity:0] [--mat-text-button-pressed-state-layer-opacity:0]'"
            (onChangeClick)="keepMyChanges()"
          >
            {{ 'board_v3_conflict_keep_mine' | translate }}
          </app-button>
        </div>
      </div>
    </div>
  }

</div>
